import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, User, Car, Clock, DollarSign, ChevronRight } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import toast from 'react-hot-toast';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #25253C;
  color: #ffffff;
  font-family: 'Clash Grotesk', sans-serif;
`;

const Header = styled.div`
  background: #25253C;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    padding: 15px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeaderStats = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 10px;
`;

const StatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  background: ${props => props.color || 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.textColor || 'rgba(255, 255, 255, 0.8)'};
  font-weight: 500;
`;

const RefreshButton = styled.button`
  background: #F43F60;
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
 
  @media (max-width: 767px) {
    margin-top: 15px;
  }
`;

const ChatList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const ChatItem = styled(motion.div)`
 background: #1E1E35;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  ${props => props.isPending && `
    background: rgba(245, 158, 11, 0.05);
    border-left: 4px solid #F59E0B;
  `}
  
  &:hover {
    background: ${props => props.isPending ? 'rgba(245, 158, 11, 0.08)' : '#131328'};
  }
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

   @media (max-width: 767px) {
    margin-bottom: 15px;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(45deg, #F43F60, rgb(247, 90, 118));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  position: relative;
`;

const ConnectionStatus = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.connected ? '#22C55E' : '#EF4444'};
  border: 2px solid #202124;
`;

const ConnectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 0.5rem;
`;

const ConnectionDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.connected ? '#22C55E' : '#EF4444'};
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: #ffffff;
`;

const UserEmail = styled.span`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
`;

const UserPhone = styled.span`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 0.25rem;
`;

const RoomCreatedAt = styled.span`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
`;

const ChatTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
`;

const RoomStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
 border-radius: 30px;
  font-size: 14px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${props => {
    switch (props.status) {
      case 'active': return 'rgba(34, 197, 94, 0.1)';
      case 'pending': return 'rgba(245, 158, 11, 0.1)';
      case 'closed': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active': return '#22C55E';
      case 'pending': return '#F59E0B';
      case 'closed': return '#EF4444';
      default: return 'rgba(255, 255, 255, 0.7)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'active': return 'rgba(34, 197, 94, 0.3)';
      case 'pending': return 'rgba(245, 158, 11, 0.3)';
      case 'closed': return 'rgba(239, 68, 68, 0.3)';
      default: return 'rgba(255, 255, 255, 0.2)';
    }
  }};
`;

const VehicleInfo = styled.div`
  background: #313151;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const VehicleIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #1E1E35;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
`;

const VehicleDetails = styled.div`
  flex: 1;
`;

const VehicleTitle = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: #fff;
  margin-bottom: 8px;
`;

const VehicleSpecs = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  span {
    background: #1E1E35;
    padding: 4px 8px;
    border-radius: 6px;
  }
`;

const LatestMessage = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const MessageContent = styled.p`
  margin: 0;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
`;

const ChatStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const OfferInfo = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 14px;
  color: #22C55E;
  font-weight: 500;

  @media (max-width: 767px) {
    margin-bottom: 15px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  padding: 2rem;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.6);
`;

const DealerChatList = () => {
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchChatHistory = async (page = 1) => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('dealerToken') || localStorage.getItem('dealerToken');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/messages/dealer/chat-history?page=${page}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch chat history');
      }

      const data = await response.json();
      setChatHistory(data.data.chatHistory);
      setPagination(data.data.pagination);
    } catch (error) {
      console.error('Error fetching chat history:', error);
      toast.error('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChatHistory();
  }, []);

  const handleChatClick = (chat) => {
    console.log("chat: ", chat);
    navigate(`/chat/${chat.roomId}`, {
      state: {
        user: chat.user,
        vehicle: chat.vehicle,
        offer: chat.offer,
        roomInfo: chat.roomInfo,
        chatStats: chat.chatStats
      }
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatRoomStatus = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'pending': return 'Pending';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  const formatRoomCreatedAt = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInDays = (now - date) / (1000 * 60 * 60 * 24);

    if (diffInDays < 1) {
      return 'Today';
    } else if (diffInDays < 2) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getChatPriority = (chat) => {
    // Priority order: pending > active > closed
    if (chat.roomInfo.status === 'pending') return 3;
    if (chat.roomInfo.status === 'active') return 2;
    if (chat.roomInfo.status === 'closed') return 1;
    return 0;
  };

  const sortChatsByPriority = (chats) => {
    return [...chats].sort((a, b) => {
      const priorityA = getChatPriority(a);
      const priorityB = getChatPriority(b);
      
      if (priorityA !== priorityB) {
        return priorityB - priorityA; // Higher priority first
      }
      
      // If same priority, sort by latest message time
      const timeA = a.latestMessage?.timestamp || a.roomInfo.createdAt || 0;
      const timeB = b.latestMessage?.timestamp || b.roomInfo.createdAt || 0;
      return new Date(timeB) - new Date(timeA);
    });
  };

  const getChatStats = () => {
    const stats = {
      total: chatHistory.length,
      pending: chatHistory.filter(chat => chat.roomInfo.status === 'pending').length,
      active: chatHistory.filter(chat => chat.roomInfo.status === 'active').length,
      closed: chatHistory.filter(chat => chat.roomInfo.status === 'closed').length,
      connected: chatHistory.filter(chat => chat.roomInfo.customerConnected).length
    };
    return stats;
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <HeaderTitle>
            <MessageCircle size={24} />
            Chat History
          </HeaderTitle>
        </Header>
        <LoadingState>
          <div>Loading chats...</div>
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <HeaderTitle>
            <MessageCircle size={24} />
            Chat History
          </HeaderTitle>
          <HeaderStats>
            <StatBadge color="rgba(245, 158, 11, 0.1)" textColor="#F59E0B">
              {getChatStats().pending} Pending
            </StatBadge>
            <StatBadge color="rgba(34, 197, 94, 0.1)" textColor="#22C55E">
              {getChatStats().active} Active
            </StatBadge>
            <StatBadge color="rgba(239, 68, 68, 0.1)" textColor="#EF4444">
              {getChatStats().closed} Closed
            </StatBadge>
            <StatBadge color="rgba(59, 130, 246, 0.1)" textColor="#3B82F6">
              {getChatStats().connected} Online
            </StatBadge>
          </HeaderStats>
        </div>
        <RefreshButton onClick={() => fetchChatHistory()}>
          Refresh
        </RefreshButton>
      </Header>

      <ChatList>
        {chatHistory.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <MessageCircle size={32} />
            </EmptyIcon>
            <h3>No Active Chats</h3>
            <p>You don't have any active chat sessions at the moment.</p>
          </EmptyState>
        ) : (
          <AnimatePresence>
            {sortChatsByPriority(chatHistory).map((chat, index) => (
              <ChatItem
                key={chat.roomId}
                isPending={chat.roomInfo.status === 'pending'}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                onClick={() => handleChatClick(chat)}
              >
                <ChatHeader>
                  <UserInfo>
                    <UserAvatar>
                      {getInitials(chat.user.name)}
                      <ConnectionStatus connected={chat.roomInfo.customerConnected} />
                    </UserAvatar>
                    <UserDetails>
                      <UserName>{chat.user.name}</UserName>
                      <UserEmail>{chat.user.email || 'No email provided'}</UserEmail>
                      {chat.user.phone && (
                        <UserPhone>📞 {chat.user.phone}</UserPhone>
                      )}
                      {chat.roomInfo.createdAt && (
                        <RoomCreatedAt>Created: {formatRoomCreatedAt(chat.roomInfo.createdAt)}</RoomCreatedAt>
                      )}
                    </UserDetails>
                  </UserInfo>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <RoomStatus status={chat.roomInfo.status}>
                      {formatRoomStatus(chat.roomInfo.status)}
                    </RoomStatus>
                    <ChatTime>
                      <Clock size={14} />
                      {formatTime(chat.latestMessage?.timestamp)}
                    </ChatTime>
                  </div>
                </ChatHeader>

                {chat.vehicle && (
                  <VehicleInfo>
                    <VehicleIcon>🚗</VehicleIcon>
                    <VehicleDetails>
                      <VehicleTitle>
                        {chat.vehicle.year} {chat.vehicle.make} {chat.vehicle.model}
                        {chat.vehicle.trim && ` ${chat.vehicle.trim}`}
                      </VehicleTitle>
                      <VehicleSpecs>
                        <span>{chat.vehicle.mileage ? `${chat.vehicle.mileage.toLocaleString()} miles` : 'N/A'}</span>
                        <span>{chat.vehicle.condition || 'Used'}</span>
                        <span>{chat.vehicle.transmission || 'N/A'}</span>
                        <span>{chat.vehicle.fuel || 'N/A'}</span>
                        <span>{formatPrice(chat.vehicle.price)}</span>
                      </VehicleSpecs>
                      {(chat.vehicle.exteriorColor || chat.vehicle.interiorColor) && (
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.25rem' }}>
                          {chat.vehicle.exteriorColor && `Exterior: ${chat.vehicle.exteriorColor}`}
                          {chat.vehicle.exteriorColor && chat.vehicle.interiorColor && ' • '}
                          {chat.vehicle.interiorColor && `Interior: ${chat.vehicle.interiorColor}`}
                        </div>
                      )}
                    </VehicleDetails>
                  </VehicleInfo>
                )}

                {chat.latestMessage && (
                  <LatestMessage>
                    <MessageContent>
                      {chat.latestMessage.content}
                    </MessageContent>
                    <MessageMeta>
                      <span>{chat.latestMessage.type === 'dealer' ? 'You' : 'Customer'}</span>
                      <span>{formatTime(chat.latestMessage.timestamp)}</span>
                    </MessageMeta>
                  </LatestMessage>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <ChatStats>
                    <StatItem>
                      <MessageCircle size={14} />
                      {chat.chatStats.totalMessages} messages
                    </StatItem>
                   
                    {chat.offer && (
                      <OfferInfo>
                        <DollarSign size={14} />
                        Offer: {formatPrice(chat.offer.amount)} ({chat.offer.status})
                        {chat.offer.createdAt && (
                          <span style={{ fontSize: '14px', opacity: 0.8 }}>
                            • {formatTime(chat.offer.createdAt)}
                          </span>
                        )}
                      </OfferInfo>
                    )}
                  </ChatStats>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ConnectionInfo>
                      <ConnectionDot connected={chat.roomInfo.customerConnected} />
                      <span>Customer</span>
                      <ConnectionDot connected={chat.roomInfo.dealerConnected} />
                      <span>Dealer</span>
                    </ConnectionInfo>
                    <ChevronRight size={16} color="rgba(255, 255, 255, 0.5)" />
                  </div>
                </div>
              </ChatItem>
            ))}
          </AnimatePresence>
        )}
      </ChatList>
    </Container>
  );
};

export default DealerChatList; 